import { tasksService } from '@/services/tasks.service';

const initialState = () => ({
  tasks: [],
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  getOpportunityTasks({ commit }, { opportunityId, silent = false }) {
    if (!silent) commit('GET_OPPORTUNITY_TASKS');
    return tasksService.getOpportunityTasks({ opportunityId }).then(
      (tasks) => {
        commit('GET_OPPORTUNITY_TASKS_SUCCESS', tasks);
        return tasks;
      },
      (error) => {
        commit('GET_OPPORTUNITY_TASKS_ERROR', error);
        throw error;
      },
    );
  },
  createOpportunityTask({ commit }, { task }) {
    commit('CREATE_OPPORTUNITY_TASK');
    return tasksService.createOpportunityTask({ task }).then(
      (created) => {
        commit('CREATE_OPPORTUNITY_TASK_SUCCESS', created);
        return created;
      },
      (error) => {
        commit('CREATE_OPPORTUNITY_TASK_ERROR', error);
        throw error;
      },
    );
  },
  saveOpportunityTaskAsTemplate({ commit }, { opportunityId, taskId }) {
    commit('UPDATE_OPPORTUNITY_TASK');
    return tasksService.saveOpportunityTaskAsTemplate({ opportunityId, taskId }).then(
      (savedTask) => {
        commit('UPDATE_OPPORTUNITY_TASK_SUCCESS', savedTask);
        return savedTask;
      },
      (error) => {
        commit('UPDATE_OPPORTUNITY_TASK_ERROR', error);
        throw error;
      },
    );
  },
  generateOpportunityTasks({ commit }, { opportunityId }) {
    commit('GENERATE_OPPORTUNITY_TASKS');
    return tasksService.generateOpportunityTasks({ opportunityId }).then(
      (tasks) => {
        commit('GET_OPPORTUNITY_TASKS_SUCCESS', tasks);
        return tasks;
      },
      (error) => {
        commit('GET_OPPORTUNITY_TASKS_ERROR', error);
        throw error;
      },
    );
  },
  updateOpportunityTask({ commit }, { task }) {
    commit('UPDATE_OPPORTUNITY_TASK');
    return tasksService.updateOpportunityTask({ task }).then(
      (updated) => {
        commit('UPDATE_OPPORTUNITY_TASK_SUCCESS', updated);
        return updated;
      },
      (error) => {
        commit('UPDATE_OPPORTUNITY_TASK_ERROR', error);
        throw error;
      },
    );
  },
  deleteOpportunityTask({ commit }, { opportunityId, taskId }) {
    commit('DELETE_OPPORTUNITY_TASK');
    return tasksService.deleteOpportunityTask({ opportunityId, taskId }).then(
      () => {
        commit('DELETE_OPPORTUNITY_TASK_SUCCESS', taskId);
      },
      (error) => {
        commit('DELETE_OPPORTUNITY_TASK_ERROR', error);
        throw error;
      },
    );
  },
  updateOpportunityTaskAssignees({ commit }, { opportunityId, taskId, assigneeIds }) {
    commit('UPDATE_OPPORTUNITY_TASK');
    return tasksService.updateOpportunityTaskAssignees({ opportunityId, taskId, assigneeIds }).then(
      (updatedTask) => {
        commit('UPDATE_OPPORTUNITY_TASK_SUCCESS', updatedTask);
        return updatedTask;
      },
      (error) => {
        commit('UPDATE_OPPORTUNITY_TASK_ERROR', error);
        throw error;
      },
    );
  },
  RESET({ commit }) {
    commit('RESET_STORE');
  },
};

const mutations = {
  GET_OPPORTUNITY_TASKS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    state.tasks = [];
  },
  GET_OPPORTUNITY_TASKS_SUCCESS(state, tasks) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.tasks = [...tasks].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  },
  GET_OPPORTUNITY_TASKS_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error,
    };
    state.tasks = [];
  },
  GENERATE_OPPORTUNITY_TASKS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  CREATE_OPPORTUNITY_TASK(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  CREATE_OPPORTUNITY_TASK_SUCCESS(state, createdTask) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.tasks = [...state.tasks, createdTask];
  },
  CREATE_OPPORTUNITY_TASK_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error,
    };
  },
  UPDATE_OPPORTUNITY_TASK(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  UPDATE_OPPORTUNITY_TASK_SUCCESS(state, updatedTask) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };

    state.tasks = state.tasks.map((t) => {
      if (t.id === updatedTask.id) {
        return updatedTask;
      }
      return t;
    });
  },
  UPDATE_OPPORTUNITY_TASK_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error,
    };
  },
  DELETE_OPPORTUNITY_TASK(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  DELETE_OPPORTUNITY_TASK_SUCCESS(state, taskId) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.tasks = state.tasks.filter((t) => t.id !== taskId);
  },
  DELETE_OPPORTUNITY_TASK_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error,
    };
  },
  // Used by task-citations.module
  UPDATE_OPPORTUNITY_TASK_CITATIONS(state, { taskId, newCitation }) {
    state.tasks = state.tasks.map((t) => {
      if (t.id === taskId) {
        return { ...t, citations: [...t.citations, newCitation] };
      }
      return t;
    });
  },
  // Used by task-citations.module
  REMOVE_OPPORTUNITY_TASK_CITATION(state, { taskId, citationId }) {
    state.tasks = state.tasks.map((t) => {
      if (t.id === taskId) {
        return { ...t, citations: t.citations.filter((c) => c.id !== citationId) };
      }
      return t;
    });
  },
  RESET_STORE(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
};

const getters = {
  tasks: (state) => state.tasks,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const tasks = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
